import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  reviewApi,
  type ReviewStatus,
  type ReviewVoteValue,
} from '../api/client';

interface DocumentStatusControlProps {
  path: string;
}

const OVERALL_LABEL: Record<ReviewStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
};

const OVERALL_BADGE_CLASS: Record<ReviewStatus, string> = {
  draft: 'border-wc-muted/40 bg-wc-gray text-wc-muted-strong',
  in_review: 'border-amber-300 bg-amber-50 text-amber-800',
  approved: 'border-emerald-300 bg-emerald-50 text-emerald-800',
};

const OVERALL_DOT_CLASS: Record<ReviewStatus, string> = {
  draft: 'bg-wc-muted',
  in_review: 'bg-amber-500',
  approved: 'bg-emerald-500',
};

/**
 * Personal-vote actions presented to reviewers. These mutate ONLY the
 * caller's vote, mirroring the GitHub-PR review model -- never the document's
 * overall status directly. The overall status is server-derived from the
 * set of all reviewer votes.
 */
const MY_VOTE_BUTTONS: Array<{ to: ReviewVoteValue; label: string }> = [
  { to: 'draft', label: 'Mark as draft' },
  { to: 'in_review', label: 'Reviewing now' },
  { to: 'approved', label: 'Approve' },
];

/**
 * Viewer-toolbar widget that surfaces the document's overall review status
 * (derived from every reviewer's vote) and, for users with the reviewer
 * role, exposes the four personal-vote actions: mark-as-draft, send-for-
 * review, approve, and clear-my-vote. Non-reviewers see only the pill.
 */
export function DocumentStatusControl({ path }: DocumentStatusControlProps) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['review-status', path],
    queryFn: () => reviewApi.getStatus(path),
    enabled: !!path,
    staleTime: 15_000,
  });

  const mutation = useMutation({
    mutationFn: (next: ReviewVoteValue) => reviewApi.setStatus(path, next),
    onSuccess: (data) => {
      qc.setQueryData(['review-status', path], data);
      // Sidebar dot reads from the tree query; statuses can flip for any
      // currently-visible folder, so invalidate the whole `tree` key space.
      qc.invalidateQueries({ queryKey: ['tree'] });
    },
  });

  if (query.isLoading) {
    return (
      <span className="inline-flex h-7 w-24 animate-pulse rounded-md bg-wc-gray/60" />
    );
  }
  if (query.isError || !query.data) {
    return null;
  }
  // Admin marked this path as excluded from the review workflow -- render
  // nothing so the toolbar looks like a regular non-reviewable file.
  if (query.data.reviewable === false) {
    return null;
  }

  const { overall, myStatus, canEdit } = query.data;

  return (
    <div className="inline-flex items-center gap-2">
      <span
        className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium ${OVERALL_BADGE_CLASS[overall]}`}
        title={`Overall review status: ${OVERALL_LABEL[overall]}`}
      >
        <span aria-hidden className={`h-2 w-2 rounded-full ${OVERALL_DOT_CLASS[overall]}`} />
        {OVERALL_LABEL[overall]}
      </span>
      {canEdit && (
        <div className="inline-flex items-center gap-1">
          {MY_VOTE_BUTTONS.map((b) => {
            const active = myStatus === b.to;
            return (
              <button
                key={b.to}
                type="button"
                onClick={() => mutation.mutate(b.to)}
                disabled={mutation.isPending || active}
                aria-pressed={active}
                className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed ${
                  active
                    ? 'border-wc-blue bg-wc-blue/10 text-wc-blue'
                    : 'border-wc-muted/30 text-wc-muted-strong hover:border-wc-blue hover:text-wc-blue disabled:opacity-60'
                }`}
                title={active ? `Your vote: ${b.label}` : `Set your vote to "${b.label}"`}
              >
                {b.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => mutation.mutate('no_response')}
            disabled={mutation.isPending || myStatus === 'no_response'}
            className="inline-flex items-center rounded-md border border-wc-muted/30 px-2.5 py-1 text-xs font-medium text-wc-muted-strong transition-colors hover:border-wc-red hover:text-wc-red disabled:cursor-not-allowed disabled:opacity-60"
            title="Clear your vote (return to 'no response')"
          >
            Clear my vote
          </button>
        </div>
      )}
      {mutation.isError && (
        <span className="text-xs text-wc-red" role="alert">
          Failed to update status.
        </span>
      )}
    </div>
  );
}
